import React, {useCallback, useEffect, useState} from 'react';
import {Flex, ScrollArea, Select, Table, TextInput} from '@mantine/core';
import {useTranslation} from 'react-i18next';
import {CpvModelAPI} from '@/domain/Cpv/CpvModelAPI';
import {CpvView} from '@/domain/Cpv/view/CpvView';

const LEVEL_VALUES = ['', '1', '2', '3', '4', '5', '6', '7'];

interface CpvTabProps {
  /** Load list only when tab is visible (sidebar CPV tab active). */
  isVisible: boolean;
}

export default function CpvTab({ isVisible }: CpvTabProps) {
  const { t, i18n } = useTranslation();
  const [cpvs, setCpvs] = useState<CpvView[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState<string | null>('');

  const lang = i18n.language?.replace('_', '-').substring(0, 2) || 'fr';

  const loadCpvs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CpvModelAPI.getList(lang);
      setCpvs(data);
    } catch {
      setCpvs([]);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    if (!isVisible) return;
    loadCpvs();
  }, [isVisible, loadCpvs]);

  const getName = (row: CpvView) => {
    if (lang === 'nl' && row.nameNl) return row.nameNl;
    if (lang === 'en' && row.nameEn) return row.nameEn;
    return row.nameFr || row.nameNl || row.nameEn || '';
  };

  const filtered = cpvs.filter((r) => {
    const matchLevel =
      levelFilter == null || levelFilter === '' || r.level === parseInt(levelFilter, 10);
    const matchText = !filter.trim() ||
      r.code?.toLowerCase().includes(filter.toLowerCase()) ||
      getName(r).toLowerCase().includes(filter.toLowerCase());
    return matchLevel && matchText;
  });

  return (
    <>
      <Flex direction="column" gap="sm" mb="sm">
        <TextInput
          placeholder={t('sidebar.cpv.searchPlaceholder')}
          value={filter}
          onChange={(e) => setFilter(e.currentTarget.value)}
        />
        <Select
          placeholder={t('sidebar.cpv.level')}
          data={LEVEL_VALUES.map((v) => ({
            value: v,
            label: v === '' ? t('sidebar.cpv.all') : v,
          }))}
          value={levelFilter}
          onChange={setLevelFilter}
          clearable
          allowDeselect
        />
      </Flex>
      <ScrollArea h="60vh" type="scroll">
        <Table striped highlightOnHover withTableBorder withColumnBorders layout="fixed">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={50}>{t('sidebar.cpv.level')}</Table.Th>
              <Table.Th w={90}>{t('sidebar.cpv.code')}</Table.Th>
              <Table.Th>{t('sidebar.cpv.description')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={3}>{t('sidebar.cpv.loading')}…</Table.Td>
              </Table.Tr>
            ) : (
              filtered.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.level}</Table.Td>
                  <Table.Td>{row.code}</Table.Td>
                  <Table.Td style={{ whiteSpace: 'normal' }}>{getName(row)}</Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </>
  );
}
